// Client for the thervumitra-site LMS plugin (/api/thervumitra/site/v1/).
// Public endpoints: plain fetch, no credentials.
import { useEffect, useState } from 'react';
import { getConfig } from '@edx/frontend-platform';

import { currentLanguage } from '../i18n';

const base = () => `${getConfig().LMS_BASE_URL}/api/thervumitra/site/v1`;

export async function getJson(path) {
  const r = await fetch(`${base()}${path}`, { headers: { Accept: 'application/json' } });
  if (r.status === 404) { return null; }
  if (!r.ok) { throw new Error(`HTTP ${r.status}`); }
  return r.json();
}

export async function postJson(path, body) {
  const r = await fetch(`${base()}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await r.json().catch(() => ({}));
  return { ok: r.ok, status: r.status, data };
}

// Loads `${path}?lang=..`; while the translation is still being made
// ("complete": false) it asks again a few times, so the text switches from
// English to the learner's language without a reload.
export function useSiteContent(path, lang = currentLanguage()) {
  const [state, setState] = useState({ loading: true, data: null, error: false });

  useEffect(() => {
    let alive = true;
    let timer = null;
    let tries = 0;
    const sep = path.includes('?') ? '&' : '?';
    const load = () => getJson(`${path}${sep}lang=${encodeURIComponent(lang)}`)
      .then((data) => {
        if (!alive) { return; }
        setState({ loading: false, data, error: false });
        const pending = data && (data.complete === false
          || (Array.isArray(data.results) && data.results.some(p => p.complete === false)));
        if (pending && tries < 6) {
          tries += 1;
          timer = setTimeout(load, 4000 * tries);
        }
      })
      .catch(() => alive && setState(s => ({ ...s, loading: false, error: !s.data })));
    setState(s => ({ ...s, loading: true }));
    load();
    return () => { alive = false; clearTimeout(timer); };
  }, [path, lang]);

  return state;
}

export function usePageTitle(title) {
  useEffect(() => {
    if (title) { document.title = `${title} | ${getConfig().SITE_NAME || 'ThervuMitra'}`; }
  }, [title]);
}

export function formatDate(value, lang = currentLanguage()) {
  const date = value && new Date(value);
  if (!date || Number.isNaN(date.getTime())) { return ''; }
  const options = { day: 'numeric', month: 'long', year: 'numeric' };
  try { return date.toLocaleDateString(lang, options); } catch (e) { return date.toLocaleDateString('en', options); }
}
